// components/rapport/common/ExportReportMenu.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import DownloadPlayersPdfButton from "../reportspages/playerposition/DownloadPlayerPositionPdfButton";

// Utils
const csvEscape = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const toCSV = (rows) => rows.map(r => r.map(csvEscape).join(",")).join("\n");

function fmtVal(v, isPct) {
  if (v == null || !Number.isFinite(v)) return "";
  return isPct ? (v * 100).toFixed(1) : Number(v).toFixed(3);
}
function computeZ(v, stat) {
  if (!stat || !Number.isFinite(stat.sd) || stat.sd <= 0 || !Number.isFinite(v)) return null;
  return (v - stat.mean) / stat.sd;
}

const buildSingleCsvRows = ({ data, metrics }) => {
  const players = data?.players || [];
  const values  = data?.values  || {};
  const byPos   = data?.by_position || {};
  const colorMap = (data?.__legend?.colorMap) || {};

  const header = [
    "dataset","metric_key","metric_label","position","player_name","value","value_is_pct",
    "z","percentile","stat_field","stat_value","color"
  ];
  const rows = [header];

  // Legend
  for (const [pos, color] of Object.entries(colorMap)) {
    rows.push(["Legend", "", "", pos, "", "", "", "", "", "", "", color]);
  }

  // ByPosition
  for (const m of metrics) {
    for (const [pos, obj] of Object.entries(byPos)) {
      const st = obj?.stats?.[m.key];
      if (!st) continue;
      for (const key of ["mean","median","sd","p10","p25","p75","p90"]) {
        rows.push(["ByPosition", m.key, m.label, pos, "", "", !!m.isPct, "", "", key, st[key] ?? "", ""]);
      }
    }
  }

  // Beeswarm
  const toPct = (v, st) => {
    if (!st || !Number.isFinite(st.p10) || !Number.isFinite(st.p90) || st.p90 <= st.p10 || !Number.isFinite(v)) return null;
    const t = (v - st.p10) / (st.p90 - st.p10);
    return Math.max(0, Math.min(1, t)) * 100;
  };
  for (const m of metrics) {
    for (const p of players) {
      const v = values[p.id]?.[m.key];
      const st = byPos[p.position]?.stats?.[m.key];
      const pct = toPct(v, st);
      rows.push(["Beeswarm", m.key, m.label, p.position || "N/A", p.name, Number.isFinite(v) ? v : "", !!m.isPct, "", pct != null ? Number(pct.toFixed(1)) : "", "", "", ""]);
    }
  }

  // Heatmap
  for (const p of players) {
    for (const m of metrics) {
      const v  = values[p.id]?.[m.key];
      const st = byPos[p.position]?.stats?.[m.key];
      const z  = computeZ(v, st);
      rows.push(["Heatmap", m.key, m.label, p.position || "N/A", p.name, Number.isFinite(v) ? v : "", !!m.isPct, Number.isFinite(z) ? Number(z.toFixed(3)) : "", "", "", "", ""]);
    }
  }

  return rows;
};

export default function ExportReportMenu({
  season,
  data,          // { players, positions, values, values_prev, by_position, __legend? }
  metrics,       // METRICS[]
  className = "",
  dark = false,  // dark mode for menu
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  // close on outside click / Esc
  useEffect(() => {
    const onClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const players = data?.players || [];
  const values  = data?.values  || {};
  const byPos   = data?.by_position || {};

  // --------- Tables for Excel / CSV ---------
  const legendRows = useMemo(() => {
    const colorMap = (data?.__legend?.colorMap) || {};
    const header = [["Position", "Color"]];
    const body = Object.entries(colorMap).map(([label, color]) => [label, color]);
    return [...header, ...body];
  }, [data]);

  const byPositionSheets = useMemo(() => {
    const sheets = {};
    for (const m of metrics) {
      const rows = [["Position","Count","Mean","Median","SD","P10","P25","P75","P90"]];
      for (const [pos, obj] of Object.entries(byPos)) {
        const st = obj?.stats?.[m.key];
        if (!st) continue;
        rows.push([pos, obj?.count ?? "", st.mean ?? "", st.median ?? "", st.sd ?? "", st.p10 ?? "", st.p25 ?? "", st.p75 ?? "", st.p90 ?? ""]);
      }
      sheets[`ByPosition.${m.key}`] = rows;
    }
    return sheets;
  }, [byPos, metrics]);

  const beeswarmSheets = useMemo(() => {
    const sheets = {};
    const toPct = (v, st) => {
      if (!st || !Number.isFinite(st.p10) || !Number.isFinite(st.p90) || st.p90 <= st.p10 || !Number.isFinite(v)) return null;
      const t = (v - st.p10) / (st.p90 - st.p10);
      return Math.max(0, Math.min(1, t)) * 100;
    };
    for (const m of metrics) {
      const rows = [["Player","Position","Value","Percentile (vs pos)"]];
      for (const p of players) {
        const v = values[p.id]?.[m.key];
        const st = byPos[p.position]?.stats?.[m.key];
        const pct = toPct(v, st);
        rows.push([p.name, p.position || "N/A", fmtVal(v, m.isPct), pct != null ? pct.toFixed(1) : ""]);
      }
      sheets[`Beeswarm.${m.key}`] = rows;
    }
    return sheets;
  }, [players, values, byPos, metrics]);

  const heatmapSheet = useMemo(() => {
    const header = ["Player","Position"];
    for (const m of metrics) {
      header.push(m.label);
      header.push(`${m.label} (z)`);
    }
    const rows = [header];
    for (const p of players) {
      const line = [p.name, p.position || "N/A"];
      for (const m of metrics) {
        const v  = values[p.id]?.[m.key];
        const st = byPos[p.position]?.stats?.[m.key];
        const z  = computeZ(v, st);
        line.push(fmtVal(v, m.isPct));
        line.push(Number.isFinite(z) ? z.toFixed(3) : "");
      }
      rows.push(line);
    }
    return rows;
  }, [players, values, byPos, metrics]);

  // --------- Handlers ---------
  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(legendRows), "Legend");
    for (const [sheetName, rows] of Object.entries(byPositionSheets)) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), sheetName.slice(0,31));
    }
    for (const [sheetName, rows] of Object.entries(beeswarmSheets)) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), sheetName.slice(0,31));
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(heatmapSheet), "Heatmap");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `players_positions_${season}.xlsx`);
    setOpen(false);
  };

  const handleExportCSVZip = async () => {
    const zip = new JSZip();
    zip.file("Legend.csv", toCSV(legendRows));
    for (const [name, rows] of Object.entries(byPositionSheets)) {
      zip.file(`${name}.csv`, toCSV(rows));
    }
    for (const [name, rows] of Object.entries(beeswarmSheets)) {
      zip.file(`${name}.csv`, toCSV(rows));
    }
    zip.file("Heatmap.csv", toCSV(heatmapSheet));

    const blob = await zip.generateAsync({ type: "blob" });
    saveAs(blob, `players_positions_${season}.zip`);
    setOpen(false);
  };

  const handleExportCSVSingle = async () => {
    const rows = buildSingleCsvRows({ data, metrics });
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `players_positions_${season}_all.csv`);
    setOpen(false);
  };

  // We still need the PDF export. We can render the provided button as a menu item.
  // It will close the menu right after click.
  const PdfItem = () => (
    <div onClick={() => setOpen(false)}>
      <DownloadPlayersPdfButton season={season} className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-md" asMenuItem />
    </div>
  );

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className={[
          "inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border shadow-sm",
          dark
            ? "bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700"
            : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50",
        ].join(" ")}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Export
        <svg width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5 7l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2"/>
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className={[
            "absolute z-50 mt-2 w-56 rounded-md border shadow-lg p-2",
            dark ? "border-gray-700 bg-gray-900 text-gray-100 shadow-black/30" : "border-gray-200 bg-white text-gray-800",
          ].join(" ")}
        >
          {/* PDF */}
          <PdfItem />

          <div className={["h-px my-2", dark ? "bg-gray-700" : "bg-gray-200"].join(" ")} />

          {/* Excel */}
          <button
            role="menuitem"
            onClick={handleExportExcel}
            className={["w-full text-left px-3 py-2 text-sm rounded-md", dark ? "hover:bg-gray-800" : "hover:bg-gray-50"].join(" ")}
          >
            Excel (.xlsx)
          </button>

          {/* CSV ZIP */}
          <button
            role="menuitem"
            onClick={handleExportCSVZip}
            className={["w-full text-left px-3 py-2 text-sm rounded-md", dark ? "hover:bg-gray-800" : "hover:bg-gray-50"].join(" ")}
          >
            CSV – plusieurs fichiers (.zip)
          </button>

          {/* CSV unique */}
          <button
            role="menuitem"
            onClick={handleExportCSVSingle}
            className={["w-full text-left px-3 py-2 text-sm rounded-md", dark ? "hover:bg-gray-800" : "hover:bg-gray-50"].join(" ")}
          >
            CSV – fichier unique
          </button>
        </div>
      )}
    </div>
  );
}
